<template>
  <div class="data-list-wrapper">
    <div id="common-data-table" class="base-table-wrapper">
      <v-data-table-server
        class="base-table sc-data-table"
        :items="itemsEntry"
        :headers="headers"
        :page="options.page"
        :items-per-page="options.itemsPerPage"
        :items-length="totalRowsEntry"
        :height="tableHeight || 'auto'"
        :show-select="allowSelect"
        :loading="loading"
        :fixed-header="fixedHeaders"
        :mobile-breakpoint="0"
        hide-default-footer
        :item-value="itemKey"
      >
        <!-- Rows come from the `item` slot below, so Vuetify draws no row checkbox and its
             selection model drives nothing else. Owning the header checkbox keeps it a pure
             function of `selected`: routed through Vuetify it desynced (`allSelected` true
             against an unchecked input), and `update:model-value` never fired on clearing.
             `v-if` because Vuetify hides its own select-all under `single-select`, but calls
             the slot regardless. -->
        <template #header.data-table-select>
          <v-checkbox-btn
            v-if="!singleSelect"
            :model-value="allPageSelected"
            :indeterminate="somePageSelected && !allPageSelected"
            @click="onSelectAll(!allPageSelected)"
          />
        </template>
        <template #header.multi="{ column: header }">
          <template
            v-for="item in header.multi"
            :key="item.value"
          >
            <CustomFieldSelect
              v-if="item.value === 'custom_field'"
              :custom-field="customField"
              :custom-field-items="customFieldItems"
              :source="source"
              @change:selected="resetSorting"
            >
              <template #default="{ label, value }">
                <span
                  class="custom-column column sortable header-span"
                  :class="{ [item.class]: item.class, 'active': isActiveSort({ value }), 'desc': isActiveSort({ value }) && !isAscendingSorting({ value }) }"
                  @click="setSort({ value, sort: getSorting({ value }), text: label })"
                >
                  <span class="mr-1">{{ label }}</span>
                  <v-icon v-if="item.icon" size="small">mdi-arrow-up</v-icon>
                </span>
              </template>
            </CustomFieldSelect>
            <div
              v-else-if="item.sortable && !disableSort"
              :key="item.text"
              style="white-space: nowrap"
              class="text-capitalize"
            >
              <span
                class="custom-column column sortable header-span"
                :class="{ [item.class]: item.class, 'active': isActiveSort(item), 'desc': isActiveSort(item) && !isAscendingSorting(item) }"
                @click="setSort({ value: item.value, sort: getSorting(item), text: item.text })"
              >
                <span class="mr-1">{{ item.text }}</span>
                <v-icon v-if="item.icon" size="small">mdi-arrow-up</v-icon>
                <AppTooltip v-if="item.tooltip" :text="item.tooltip" wrap>
                  <template #default="{ props }">
                    <v-icon
                      v-bind="props"
                      size="14"
                      data-test="header-tooltip"
                      @click.stop
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                </AppTooltip>
              </span>
            </div>
            <div
              v-else
              :key="item.text"
              style="white-space: nowrap"
              :class="item.class ? item.class : ''"
              class="text-capitalize"
            >
              <div v-if="item.multi">
                <template v-if="item.groupName">
                  {{ item.groupName }}:
                </template>
                <span v-for="(subItem) in item.multi" :key="subItem.value" class="sub-item">
                  <span
                    v-if="subItem.sortable && !disableSort"
                    :key="subItem.text"
                    style="white-space: nowrap"
                    class="column sortable header-span column--pointer mr-1"
                    :class="{ [subItem.class]: subItem.class, 'active': isActiveSort(subItem), 'desc': isActiveSort(subItem) && !isAscendingSorting(subItem) }"
                    @click="setSort({ value: subItem.value, sort: getSorting(subItem), text: subItem.text })"
                  >
                    <div class="d-flex align-center">
                      {{ subItem.text }}<template v-if="subItem.text === 'City' && !isActiveSort(subItem)">,&nbsp;</template><template v-else-if="subItem.text === 'State' && !isActiveSort(subItem)">,&nbsp;</template>
                      <v-icon v-if="isActiveSort(subItem) && subItem.icon" size="small">mdi-arrow-up</v-icon>
                      <template v-if="subItem.text === 'City' && isActiveSort(subItem)">,&nbsp;</template>
                    </div>
                  </span>
                  <span v-else>
                    {{ subItem.text }}
                  </span>
                </span>
              </div>
              <span v-else>{{ item.text }}</span>
            </div>
          </template>
        </template>
        <template #header.views="{ column: header }">
          <span class="views-span d-print-none text-capitalize">
            {{ header.text }}
          </span>
        </template>
        <template #top>
          <v-expand-transition>
            <div v-show="selected.length" class="base-table__selection-bar">
              <div class="pa-2">
                <span v-if="selected.length === totalRowsEntry">
                  All <span class="base-table__number">{{ selected.length }}</span> items are selected.
                </span>
                <span v-if="selected.length !== totalRowsEntry">
                  <span class="base-table__number">{{ selected.length }}</span> items on this page are selected.
                  <v-progress-circular
                    v-if="loading"
                    color="primary"
                    indeterminate
                    size="20"
                    class="ml-1"
                    width="2"
                  />
                  <span v-else-if="allowShowCheckAll" class="base-table__check-all" @click="$emit('checkAll')">
                    Select all {{ totalRowsEntry }} items
                  </span>
                  <v-btn icon title="Clear Selected" @click="unCheckAll"><v-icon>mdi-close-circle</v-icon></v-btn>
                </span>
              </div>
            </div>
          </v-expand-transition>
        </template>
        <template #item="{ item }">
          <slot
            name="item"
            :item="item"
            :selected-key-set="selectedKeySet"
          />
        </template>
        <template #body.append="{ items, columns }">
          <template v-if="items.length && $slots['footer-summary']">
            <!-- Takes the height the rows leave in a fitted table, so the totals sit at the
                 bottom of the table instead of under the last row -->
            <tr class="base-table__filler" aria-hidden="true">
              <td :colspan="columns.length" />
            </tr>
            <slot name="footer-summary" />
          </template>
        </template>
      </v-data-table-server>
    </div>
    <v-toolbar v-show="!hidePagination" class="base-table-pagination">
      <v-pagination
        v-model="options.page"
        :length="pageCount || 0"
        :disabled="pageCount === 0 || loading"
        :total-visible="$vuetify.display.xs ? 3 : 7"
        class="pa-2"
        width="800"
      />
      <v-menu location="bottom start">
        <template #activator="{ props }">
          <v-btn
            variant="text"
            v-bind="props"
            :size="$vuetify.display.xs ? 'small' : undefined"
            :class="{ 'px-1': $vuetify.display.xs }"
          >
            {{ options.itemsPerPage }} per page
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="(item, index) in options.itemsPerPageOptions"
            :key="index"
            link
            @click="updatePerPage(item)"
          >
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-toolbar>
  </div>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue';

import { ITableOptions, IHeader } from '@/bundles/BaseTable/interfaces';
import { defaultItemsPerPageOptions } from '@/bundles/BaseTable/helpers/consts';
import { withHeaderTitles } from '@/bundles/BaseTable/helpers/withHeaderTitles';
import { ITextValue } from '@/types/common';
import { SourceTypeEnum } from '@/bundles/App/enums';

import AppTooltip from '@/bundles/App/components/common/AppTooltip.vue';

import CustomFieldSelect from './CustomFieldSelect.vue';

interface IBaseTableInstance {
  options: ITableOptions,
  footerOptions: {
    itemsPerPageText: string;
    disableItemsPerPage: boolean;
    itemsPerPageOptions: number[];
    showFirstLastPage: boolean;
  };
}

export default defineComponent({
  name: 'BaseTable',

  components: {
    AppTooltip,
    CustomFieldSelect,
  },

  props: {
    tableOptionsEntry: {
      type: Object as PropType<ITableOptions>,
      required: true,
    },
    itemsEntry: {
      type: Array,
      default: () => [],
    },
    totalRowsEntry: {
      type: Number,
      default: 0,
    },
    headersDataEntry: {
      type: Array as PropType<IHeader[]>,
      required: true,
    },
    selected: {
      type: Array,
      default: () => [],
    },
    tableHeight: {
      type: Number,
      default: null,
    },
    allowSelect: Boolean,
    loading: Boolean,
    fixedHeaders: Boolean,
    hidePagination: Boolean,
    allowShowCheckAll: {
      type: Boolean,
      default: true,
    },
    disableSort: Boolean,
    isMergeTable: Boolean,
    itemKey: {
      type: String,
      default: '_key',
    },
    customField: {
      type: String,
      default: null,
    },
    customFieldItems: {
      type: Array as PropType<ITextValue<string>[]>,
      default: () => [] as ITextValue<string>[],
    },
    source: {
      type: String as PropType<string | SourceTypeEnum>,
      default: null,
    },
    singleSelect: Boolean,
  },

  emits: ['changeSelected', 'checkAll', 'options:change', 'uncheckAll'],

  data: (): IBaseTableInstance => ({
    options: {
      groupBy: [],
      itemsPerPage: 50,
      multiSort: false,
      mustSort: false,
      page: 1,
      sortBy: [],
      sortDesc: [],
      itemsPerPageOptions: defaultItemsPerPageOptions
    },
    footerOptions: {
      itemsPerPageText: '',
      disableItemsPerPage: true,
      itemsPerPageOptions: [10, 25, 100],
      showFirstLastPage: true,
    },
  }),

  computed: {
    headers (): IHeader[] {
      // Sorting here is server-side and opt-in: only a header that says
      // `sortable: true` gets the sort control, as in Vue 2 (the helper's
      // `key` would otherwise make every keyed column sortable).
      return withHeaderTitles(this.headersDataEntry).map(header => ({ sortable: false, ...header }));
    },
    pageCount: function (): number {
      if (this.totalRowsEntry <= this.options.itemsPerPage) {
        return 1;
      }

      return Math.ceil(this.totalRowsEntry / this.options.itemsPerPage);
    },
    // Callers hold either shape: merge tables assign `selected = [...items]`, list pages
    // assign keys.
    selectedKeys (): unknown[] {
      return this.selected.map((item) => (
        typeof item === 'object' && item !== null ? (item as Record<string, any>)[this.itemKey] : item
      ));
    },
    // Built once per `selected` change so per-row lookups (here and in the `item` slot) are
    // O(1) instead of an O(selected.length) scan each.
    selectedKeySet (): ReadonlySet<unknown> {
      return new Set(this.selectedKeys);
    },
    allPageSelected (): boolean {
      return !!this.itemsEntry.length &&
        (this.itemsEntry as Record<string, any>[]).every((item) => this.selectedKeySet.has(item[this.itemKey]));
    },
    somePageSelected (): boolean {
      return (this.itemsEntry as Record<string, any>[]).some((item) => this.selectedKeySet.has(item[this.itemKey]));
    }
  },

  watch: {
    options: {
      handler: function (value) {
        this.$emit('options:change', value);
      },
      deep: true,
    },
    tableOptionsEntry: {
      handler: function (value) {
        if (JSON.stringify(value) !== JSON.stringify(this.options)) {
          this.options = { ...value };
        }
      },
      deep: true,
      immediate: true
    },
  },

  created () {
    this.$eventBus.$emit('refresh:table-height');
  },

  mounted () {
    this.$eventBus.$on('filter-change', this.unCheckAll);
  },

  beforeUnmount () {
    this.$eventBus.$off('filter-change', this.unCheckAll);
  },

  methods: {
    updatePerPage (item) {
      this.options.itemsPerPage = item.value
      this.options.page = 1
    },
    isActiveSort: function (item: IHeader): boolean {
      const sortBy = Array.isArray(this.options.sortBy)
        ? this.options.sortBy[0]
        : this.options.sortBy;
      const valueText = item.value;
      return sortBy === valueText;
    },
    setSort: function ({ value, sort, text }: { value: string; sort: boolean, text: string }) {
      this.options = { ...this.options, sortBy: [value], sortDesc: [sort], sortText: text };
      document.querySelector('.base-table .v-table__wrapper')!.scrollTop = 0
    },

    // Replaces Vuetify 2's `toggle-select-all`, which Vuetify 3 removed.
    //
    // The emitted payload stays the Vuetify 2 shape: whole rows. Callers convert it themselves
    // (`ContactsListTable` maps `data.map(item => item._key)` before storing it), so handing
    // over keys would leave them mapping `_key` off a string.
    onSelectAll: function (value: boolean) {
      if (!value) {
        this.$emit('uncheckAll');

        return;
      }

      // `checkAll` is the merge table's own select-all path, kept so its handler still runs.
      this.$emit(this.isMergeTable ? 'checkAll' : 'changeSelected', [...this.itemsEntry]);
    },
    unCheckAll () {
      this.$emit('uncheckAll');
    },
    getSorting (item: IHeader): boolean {
      return this.isActiveSort(item) ? !this.options.sortDesc?.[0] : true;
    },
    isAscendingSorting (item: IHeader): boolean {
      return this.isActiveSort(item) ? !this.options.sortDesc?.[0] : false;
    },
    resetSorting () {
      this.options = { ...this.options, sortBy: [], sortDesc: [], sortText: undefined };
    }
  },
});
</script>

<style lang="scss" scoped>
.base-table-wrapper {
  padding-bottom: 0;
  overflow: auto;
}

.base-table {
  // The bar sits in the table's `top` slot, so it takes its space the moment a row is
  // selected. A fade left that jump untouched and only faded the text into it; the expand
  // transition animates the height, so the table below slides instead of stepping.
  //
  // The padding belongs to the inner element on purpose: the transition animates height
  // alone, so padding on the animated element would floor the collapse at 16px and snap
  // the last frame away.
  &__selection-bar {
    background-color: rgb(var(--v-theme-surface-light));
  }

  &__check-all {
    cursor: pointer;
    color: #1E88E5;
    transition: all ease .3s;
    font-size: 16px;
    font-weight: 600;
    text-align: center;
  }

  &__number {
    font-weight: 600;
    color: #1E88E5;
  }

  // A table with totals fills its fitted wrapper; the filler row is the only one that
  // grows (rows of a full-height table share the spare height otherwise). In an
  // auto-height table 100% resolves to auto, so the filler stays 0 there.
  :deep(table:has(.base-table__filler)) {
    height: 100%;
  }

  &__filler {
    height: 100%;

    // Vuetify gives every cell the 48px row height; the filler has no height of its own
    > td {
      height: auto !important;
      padding: 0 !important;
      border: 0 !important;
    }
  }

  // The totals row stays at the bottom while a long list scrolls under it. The row's
  // background is on the tr, which sticky cells do not paint, so they inherit it.
  :deep(.base-table__filler ~ tr > td) {
    position: sticky;
    bottom: 0;
    z-index: 1;
    background-color: inherit;
  }
}

.custom-column {
  &.sortable {
    &.active {
      color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity))!important;
    }
  }
}

.column {
  &--pointer {
    cursor: pointer;
  }
}

.sub-item {
  display: inline-block;
  margin-left: 4px;

  &:first-of-type {
    margin-left: 0;
  }
}
</style>
